import React, { useCallback, useEffect, useRef, useState } from "react";

interface TurnTimerProps {
    durationMs: number;
    onExpire?: () => void;
    endingTimestamp: number;
    isActive: boolean;
    color?: string;
    height?: number;
    topValue?: number;
    showTimeoutCount?: number;
    showNumber: boolean;
}

const TurnTimer: React.FC<TurnTimerProps> = ({
    durationMs,
    onExpire,
    endingTimestamp,
    isActive,
    color = "#4caf50",
    height = 4,
    topValue = 0,
    showTimeoutCount = -1,
    showNumber = true,
}) => {
    const [remaining, setRemaining] = useState(endingTimestamp - Date.now());
    const startTimeRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        startTimeRef.current = performance.now();

        if (!isActive) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            setRemaining(endingTimestamp - startTimeRef.current);
            return;
        }

        const tick = (_: number) => {
            const newRemaining = endingTimestamp - Date.now();
            setRemaining(newRemaining);

            if (newRemaining <= 0) {
                onExpire?.();
            } else {
                rafRef.current = requestAnimationFrame(tick);
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [isActive, durationMs, onExpire, endingTimestamp]);

    const progress = (remaining / durationMs) * 100;

    return (
        <div
            style={{
                position: "fixed",
                top: topValue,
                left: showTimeoutCount !== 0 ? "20px" : "0px",
                right: 0,
                height: `${height}px`,
                backgroundColor: "#424242",
                zIndex: 0,
            }}
        >
            {showTimeoutCount > 0 && showNumber ? <div
                style={{
                    position: "fixed",
                    top: topValue - 10,
                    left: "5px",
                    color: color,
                }}
            >
                {showTimeoutCount}
            </div> : ""}
            <div
                style={{
                    height: "100%",
                    width: `${isActive ? progress : 0}%`,
                    borderRadius: `0px 10px 10px 0px`,
                    background: color,
                    zIndex: 0,
                }}
            />
        </div>
    );
};


// Hook to manage timer state alongside your existing handlers
export const useTurnTimer = (durationMs: number = 60000) => {
    const [isActive, setIsActive] = useState(false);
    const [endingTimestamp, setEndingTimestamp] = useState<number>(0);

    const startTimer = useCallback((endingTimestamp: number) => {
        setIsActive(true);
        setEndingTimestamp(endingTimestamp)
    }, []);

    const timerProps: any = {
        durationMs,
        endingTimestamp,
        isActive,
    };

    const cancelTimer = useCallback(() => setIsActive(false), []);
    return { startTimer, cancelTimer, timerProps, isActive };
};

export default TurnTimer;